import React, { useState, useMemo } from 'react';
import './StoreCompareSheet.css';

const TIER_LABELS = { basic: 'Value', standard: 'Standard', premium: 'Premium', organic: 'Organic' };
const formatTier  = t => TIER_LABELS[t?.toLowerCase()] || null;

// Shared price lookup — same normalisation as ShoppingList
const getItemPrice = (item, storeId) => {
  if (!item.prices?.length) return null;
  const entry = item.prices.find(p => {
    const normId = (p.storeId || '').replace(/^store_/, '');
    return normId === storeId || p.storeId === storeId;
  });
  return entry?.price > 0 ? entry.price : null;
};

// Smart pre-selection: returns [storeIdA, storeIdB]
function getDefaultPair(items, stores) {
  if (!items.length || !stores.length) return [];

  const matchCount = {};
  const total      = {};
  stores.forEach(s => { matchCount[s.id] = 0; total[s.id] = 0; });
  items.forEach(item => {
    stores.forEach(s => {
      const p = getItemPrice(item, s.id);
      if (p != null) {
        matchCount[s.id]++;
        total[s.id] += p * (item.quantity || 1);
      }
    });
  });

  const eligible  = stores.filter(s => matchCount[s.id] > 0);
  if (eligible.length < 2) return eligible.map(s => s.id);

  const complete  = eligible.filter(s => matchCount[s.id] === items.length);
  const partial   = eligible
    .filter(s => matchCount[s.id] < items.length)
    .sort((a, b) => (matchCount[b.id] - matchCount[a.id]) || (total[a.id] - total[b.id]));

  if (complete.length === 0) {
    return [partial[0].id, partial[1].id];
  }
  if (complete.length === 1) {
    const second = partial[0] ?? eligible.find(s => s.id !== complete[0].id);
    return [complete[0].id, second?.id].filter(Boolean);
  }
  // Multiple complete: cheapest + second cheapest
  const sorted = [...complete].sort((a, b) => total[a.id] - total[b.id]);
  return [sorted[0].id, sorted[1].id];
}

export default function StoreCompareSheet({ items, stores, onClose }) {
  const defaultPair              = useMemo(() => getDefaultPair(items, stores), []);
  const [selectedIds, setSelectedIds] = useState(defaultPair);
  const [pickerSlot, setPickerSlot]   = useState(null); // 0 | 1 | null

  // Per-column totals + coverage
  const columnData = selectedIds.map(storeId => {
    let colTotal = 0, matched = 0;
    items.forEach(item => {
      const p = getItemPrice(item, storeId);
      if (p != null) { colTotal += p * (item.quantity || 1); matched++; }
    });
    return { total: colTotal, matched };
  });

  // Build matrix rows
  const rows = useMemo(() => items.map(item => {
    const prices           = selectedIds.map(sid => getItemPrice(item, sid));
    const neitherAvailable = prices.every(p => p == null);
    const bothAvailable    = prices.every(p => p != null);
    const minPrice         = bothAvailable ? Math.min(...prices) : null;
    return { item, prices, neitherAvailable, minPrice };
  }), [items, selectedIds]);

  const mainRows = rows.filter(r => !r.neitherAvailable);
  const greyRows = rows.filter(r =>  r.neitherAvailable);

  // Eligible stores for the switcher (has ≥1 matched item, not the other selected store)
  const getEligible = slot => {
    const otherId = selectedIds[slot === 0 ? 1 : 0];
    return stores.filter(s => {
      if (s.id === otherId) return false;
      return items.some(item => getItemPrice(item, s.id) != null);
    });
  };

  const handleSwap = newId => {
    const updated = [...selectedIds];
    updated[pickerSlot] = newId;
    setSelectedIds(updated);
    setPickerSlot(null);
  };

  return (
    <>
      <div className="sheet-backdrop" onClick={onClose} />

      <div className="cmp-sheet">

        {/* ── Handle + title ── */}
        <div className="cmp-header">
          <div className="cmp-handle" />
          <div className="cmp-title-row">
            <span className="cmp-title">Compare stores</span>
            <button className="cmp-close" onClick={onClose} aria-label="Close">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="4" y1="4" x2="14" y2="14" /><line x1="14" y1="4" x2="4" y2="14" />
              </svg>
            </button>
          </div>
        </div>

        {/* ── Store column headers ── */}
        <div className="cmp-col-header">
          <div className="cmp-item-col" />
          {selectedIds.map((storeId, i) => {
            const store = stores.find(s => s.id === storeId);
            return (
              <button
                key={storeId || i}
                className={`cmp-store-btn${pickerSlot === i ? ' cmp-store-btn--active' : ''}`}
                onClick={() => setPickerSlot(pickerSlot === i ? null : i)}
              >
                <span className="cmp-store-btn-name">{store?.name || 'Choose'}</span>
                <svg className="cmp-chevron" width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="2 3 5 7 8 3" />
                </svg>
              </button>
            );
          })}
        </div>

        {/* ── Totals row ── */}
        <div className="cmp-totals-row">
          <div className="cmp-item-col cmp-totals-label">Total</div>
          {selectedIds.map((sid, i) => (
            <div key={sid} className="cmp-total-cell">
              <span className="cmp-total-price">£{columnData[i].total.toFixed(2)}</span>
              <span className="cmp-total-coverage">{columnData[i].matched}/{items.length}</span>
            </div>
          ))}
        </div>

        <div className="cmp-divider-line" />

        {/* ── Item rows (scrollable) ── */}
        <div className="cmp-rows">
          {mainRows.map(({ item, prices, minPrice }) => (
            <div key={item.id || item.name} className="cmp-row">
              <div className="cmp-item-col cmp-row-item">
                <span className="cmp-item-name">{item.name}</span>
                {(formatTier(item.tier) || (item.quantity > 1)) && (
                  <span className="cmp-item-meta">
                    {[formatTier(item.tier), item.quantity > 1 ? `×${item.quantity}` : null].filter(Boolean).join(' · ')}
                  </span>
                )}
              </div>
              {prices.map((price, i) => {
                const isCheap = price != null && minPrice != null && price === minPrice && prices[0] !== prices[1];
                return (
                  <div
                    key={i}
                    className={[
                      'cmp-price-cell',
                      isCheap   ? 'cmp-price-cell--cheapest'   : '',
                      price == null ? 'cmp-price-cell--na'   : '',
                    ].join(' ').trim()}
                  >
                    {price != null
                      ? isCheap
                        ? <span className="cmp-price-pill">£{price.toFixed(2)}</span>
                        : `£${price.toFixed(2)}`
                      : '—'
                    }
                  </div>
                );
              })}
            </div>
          ))}

          {greyRows.length > 0 && (
            <div className="cmp-grey-divider">Not available in either store</div>
          )}
          {greyRows.map(({ item }) => (
            <div key={item.id || item.name} className="cmp-row cmp-row--grey">
              <div className="cmp-item-col cmp-row-item">
                <span className="cmp-item-name">{item.name}</span>
              </div>
              <div className="cmp-price-cell cmp-price-cell--na">—</div>
              <div className="cmp-price-cell cmp-price-cell--na">—</div>
            </div>
          ))}

          <div className="cmp-rows-end-pad" />
        </div>

        {/* ── Store switcher picker ── */}
        {pickerSlot !== null && (
          <>
            <div className="cmp-picker-backdrop" onClick={() => setPickerSlot(null)} />
            <div className="cmp-picker">
              <div className="cmp-handle" />
              <p className="cmp-picker-title">Switch store</p>
              {getEligible(pickerSlot).map(store => {
                const isCurrent = store.id === selectedIds[pickerSlot];
                return (
                  <button
                    key={store.id}
                    className={`cmp-picker-row${isCurrent ? ' cmp-picker-row--current' : ''}`}
                    onClick={() => handleSwap(store.id)}
                  >
                    <span>{store.name}</span>
                    {isCurrent && (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 8 7 12 13 4" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}

      </div>
    </>
  );
}
